import { useState } from 'react';

import { Typography } from '@design-system/components/typography/typography';
import { FormDataMultiSelect } from '../viewModel/otpBundles.viewModel';
import { isEmpty } from 'lodash';
import { DropDownMultiselect } from '@client/new/pages/bundles/otp/view/dropDownMultiselect/dropDownMultiselect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sprinkles } from '@design-system/sprinkles.css';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';

import { StatusTag } from '@client/new/pages/programs/view/components/StatusTag';
import { ProgramReadinessPopover } from '@client/new/pages/programs/view/components/ProgramReadinessPopover';
import { ProgramReadiness } from '@client/new/pages/programs/model/programs.types';

export const ContentSection = <T extends string | number>({
  sectionTitle,
  emptyState,
  options,
  selectedItems,
  disabled,
  value,
  onChange,
  toFormOnChange,
}: FormDataMultiSelect<T> & {
  disabled: boolean;
  value: T[];
  onChange: (value: T[]) => void;
}) => {
  if (isEmpty(options)) {
    return (
      <div className="flex flex-col gap-4">
        <Typography variant="body" size="large">
          {sectionTitle}
        </Typography>
        <div className="flex gap-1 items-center">
          <Typography
            variant="body"
            size="medium"
            color="N-80"
            className="underline cursor-pointer"
            onClick={emptyState.onClick}
          >
            {emptyState.urlLabel}
          </Typography>
          <Typography variant="body" size="medium" color="N-80">
            {emptyState.label}
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Typography
        variant="body"
        size="large"
        color={disabled ? 'N-60' : 'B-100'}
      >
        {sectionTitle}
      </Typography>
      <DropDownMultiselect
        items={options}
        disabled={disabled}
        value={value}
        onChange={onChange}
        toFormOnChange={toFormOnChange}
      />
      <ContentList
        selectedItems={selectedItems}
        disabled={disabled}
        onChange={onChange}
        toFormOnChange={toFormOnChange}
        value={value}
      />
    </div>
  );
};

const ContentList = <T extends string | number>({
  selectedItems,
  disabled,
  onChange,
  toFormOnChange,
  value,
}: Pick<FormDataMultiSelect<T>, 'selectedItems' | 'toFormOnChange'> & {
  disabled: boolean;
  value: T[];
  onChange: (value: T[]) => void;
}) => {
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverReadiness, setPopoverReadiness] =
    useState<ProgramReadiness | null>(null);

  const openPopover = (anchor: HTMLElement, readiness: ProgramReadiness) => {
    setPopoverAnchor(anchor);
    setPopoverReadiness(readiness);
  };
  const closePopover = () => setPopoverAnchor(null);

  return (
    <div>
      {selectedItems.map((item) => (
        <div
          key={item.id}
          className="flex justify-between border-b-[1px] border-[#E2E2E2] py-2 gap-2 items-center"
        >
          <div className="flex items-center gap-3 overflow-x-hidden">
            <Typography
              color={disabled ? 'N-60' : 'B-100'}
              className="overflow-x-hidden"
            >
              {item.name}
            </Typography>
            {item.status ? (
              <StatusTag
                state={item.status.state}
                readiness={item.status.readiness}
                onReadinessClick={
                  item.status.readiness
                    ? (anchor) => openPopover(anchor, item.status!.readiness!)
                    : undefined
                }
              />
            ) : null}
          </div>
          {!(disabled || item.disableDelete) && (
            <div className="flex">
              <FontAwesomeIcon
                icon={faTrash}
                onClick={() => {
                  const newValues = toFormOnChange(item.id, value || []);
                  onChange(newValues);
                }}
                className={sprinkles({
                  cursor: 'pointer',
                  color: 'B-100',
                })}
              />
            </div>
          )}
        </div>
      ))}
      <ProgramReadinessPopover
        anchor={popoverAnchor}
        readiness={popoverReadiness}
        onClose={closePopover}
      />
    </div>
  );
};
